import {JSONSchema7} from "json-schema";
import {omit} from "lodash";
import {Playshot} from "../../../../model/Playshot";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {
  ThenDeleteProjection,
  ThenInsertProjection, ThenReplaceProjection,
  ThenUpdateProjection,
  ThenUpsertProjection
} from "../../../cody-wizard/rule-engine/configuration";
import {Shorthand} from "../../../cody-wizard/schema/shorthand";
import {UiSchema} from "../../../cody-wizard/ui/ui-schema";
import {ProjectionConfig, ProjectionConfigCase} from "../../../cody-wizard/vo/get-vo-metadata";
import {
  convertJsonSchemaToShorthandIfPossible
} from "../../../json/schema/convert-json-schema-to-shorthand-if-possible";
import {playwithErrorCheck} from "../../cody-error";
import {ProophBoardDescriptionKeys, ResolveConfig} from "../../descriptions";
import {AnyRule, ThenType} from "../../rule-definitions";
import {PlayEventPolicyDescription} from "../policies/policy-definitions";
import {PlayInformationRuntimeInfo, PlayQueryRuntimeInfo} from "./information-definitions";
import {cloneDeepJSON} from "../../../json/json-comment-wrapper";

export interface PreparedInformation {
  desc: object,
  schema: Shorthand | JSONSchema7,
  initialize: AnyRule[],
  uiSchema?: UiSchema,
  querySchema?: Shorthand | JSONSchema7,
  resolve?: ResolveConfig,
  projection?: ProjectionConfig
}

export const prepareDiffInformation = (information: PlayInformationRuntimeInfo, playshot: Playshot): PreparedInformation => {
  let queryInfo: PlayQueryRuntimeInfo | undefined;
  let resolve: ResolveConfig | undefined;

  Object.values(playshot.playConfig.queries).forEach(qInfo => {
    if (qInfo.desc.returnType === information.desc.name) {
      queryInfo = qInfo;
    }
  })

  if(queryInfo) {
    resolve = playshot.playConfig.resolvers[queryInfo.desc.name];
  }

  return {
    desc: omit(information.desc, ProophBoardDescriptionKeys),
    schema: convertJsonSchemaToShorthandIfPossible(information.schema),
    uiSchema: information.uiSchema,
    initialize: information.factory,
    querySchema: queryInfo ? convertJsonSchemaToShorthandIfPossible(queryInfo.schema) : undefined,
    resolve,
    projection: collectProjectionConfig(information, playshot)
  }
}

const collectProjectionConfig = (information: PlayInformationRuntimeInfo, playshot: Playshot): ProjectionConfig | undefined => {
  if(!information.desc.projection) {
    return;
  }

  const prjName = information.desc.projection;

  const cases: ProjectionConfigCase[] = [];

  let live = true;

  // tslint:disable-next-line:forin
  for (const eventName in playshot.playConfig.eventPolicies) {
    const eventPolicies = playshot.playConfig.eventPolicies[eventName];

    if(eventPolicies[prjName]) {
      live = !!eventPolicies[prjName].live;
      cases.push(playwithErrorCheck(convertEventProjectionCaseToProjectionConfig, [{eventName, desc: eventPolicies[prjName]}]));
    }
  }

  if(cases.length === 0) {
    return;
  }

  return {
    name: prjName,
    live,
    cases
  }
}

const convertEventProjectionCaseToProjectionConfig = (eventCase: {eventName: string, desc: PlayEventPolicyDescription}): ProjectionConfigCase | CodyResponse => {
  eventCase = cloneDeepJSON(eventCase);

  const rules = eventCase.desc.rules;

  const thenRule = rules.pop();

  if(!thenRule) {
    return {
      cody: `Can't resolve projection configuartion for "${eventCase.desc.projection}". The event case "${eventCase.eventName}" has no rules defined.`,
      details: `Either the playshot config is broken or it is a bug in Cody Play. If you can't resolve the issue yourself, please contact the prooph board team!`,
      type: CodyResponseType.Error
    }
  }

  const given = rules.length > 0 ? rules : undefined;
  const then = playwithErrorCheck(convertThenProjection, [thenRule.then, eventCase]);

  return {
    given,
    when: eventCase.eventName,
    then
  }
}

const allowedOperations = ['insert', 'upsert', 'update', 'replace', 'delete'];

const convertThenProjection = (then: ThenType, eventCase: {eventName: string, desc: PlayEventPolicyDescription}): ThenInsertProjection | ThenUpsertProjection | ThenUpdateProjection | ThenReplaceProjection | ThenDeleteProjection | CodyResponse => {
  const operation = Object.keys(then).pop() || '';

  if(!allowedOperations.includes(operation)) {
    return {
      cody: `Can't resolve projection configuartion for "${eventCase.desc.projection}". The event case "${eventCase.eventName}" has no valid rules defined.`,
      details: `Either the playshot config is broken or it is a bug in Cody Play. If you can't resolve the issue yourself, please contact the prooph board team!`,
      type: CodyResponseType.Error
    }
  }

  return {
    [operation]: omit((then as any)[operation], "information")
  } as ThenInsertProjection | ThenUpsertProjection | ThenUpdateProjection | ThenReplaceProjection | ThenDeleteProjection;
}


